import type { MoneyFormatter } from "../../shared/money";
import { toNumber } from "../../shared/money";
import type { ItemPricing, MarkupTier, PricingRule, GroupRow } from "./meta";
import { type Basis, shownAmount } from "./pricing";

/** "20 min", "2 hr", "1.5 hr". */
export function duration(minutes: number) {
    if (minutes < 60) return `${Math.round(minutes * 100) / 100} min`;
    // "1 hr 6 min", not "1.1 hr": to the minute.
    const whole = Math.round(minutes);
    const hours = Math.floor(whole / 60);
    return whole % 60 ? `${hours} hr ${whole % 60} min` : `${hours} hr`;
}

/** A markup group's tiers in a few words: "Cost + 40%", or tiered "Cost + 200% to 30%, by cost". */
function describeTiers(tiers: readonly MarkupTier[], fmt: MoneyFormatter) {
    const [first, ...rest] = tiers;
    const last = rest.at(-1);
    if (!last) return `Cost + ${fmt.percent(toNumber(first?.markup ?? "0"))}`;
    return `Cost + ${fmt.percent(toNumber(first?.markup ?? "0"))} to ${fmt.percent(toNumber(last.markup))}, by cost`;
}

/** How an item's price is set, in a few words: "Typed", "Manual $40.00", "Cost + 35%", "Cost + 200% to 30%, by cost". */
export function describePricing(rule: PricingRule | undefined, basis: Basis, fmt: MoneyFormatter): string {
    if (!rule) return "Typed";
    if (rule.mode === "manual") return `Manual ${fmt.money(shownAmount(rule.basePrice, basis))}`;
    return rule.tiers?.length ? describeTiers(rule.tiers, fmt) : `Cost + ${fmt.percent(toNumber(rule.markupValue))}`;
}

/** A pricing group in a few words: a fixed price as its pricing and tax ("Fixed $249.00 · inc GST"), a markup as its tiers. `taxText`: a fixed price's tax, as it's said ("inc GST"). */
export function describeGroup(s: Pick<GroupRow, "rule" | "tiers">, basis: Basis, taxText: string, fmt: MoneyFormatter): string {
    return s.tiers ? describeTiers(s.tiers, fmt) : `Fixed ${fmt.money(shownAmount(s.rule.basePrice, basis))} · ${taxText}`;
}

/** The same, short enough for a table column: "Typed price", "Linked to Catalogue", "Manual", "+35%", "Tiered markup", "30 min labour". */
export function describePricingShort(rule: (PricingRule & Pick<ItemPricing, "labour" | "fromParts">) | undefined, fmt: MoneyFormatter): string {
    if (!rule) return "Typed price";
    if (rule.labour) return `${duration(rule.labour.minutes)} labour`;
    if (rule.fromParts) return "Linked to Catalogue";
    if (rule.mode === "manual") return "Manual";
    if (rule.tiers && rule.tiers.length > 1) return "Tiered markup";
    const pct = toNumber(rule.tiers?.[0]?.markup ?? rule.markupValue);
    return pct ? `+${fmt.percent(pct)}` : "Cost";
}
